import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchTrainingImageBlobUrl,
  isExternalImageUrl,
  revokeTrainingImageBlobUrl,
  shouldLoadTrainingImageViaApi,
} from '../utils/trainingImage';
import {
  Box, Typography, Button, Card, CardContent, Alert, Chip,
  LinearProgress, Paper, Grid, IconButton
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Quiz as QuizIcon,
  VideoLibrary as VideoIcon,
  Description as TextIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  PictureAsPdf as DocumentIcon,
  Clear as ClearIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  FastForward as FastForwardIcon,
  Speed as SpeedIcon,
  VolumeUp as VolumeUpIcon,
  Stop as StopIcon
} from '@mui/icons-material';

// YouTube API TypeScript declarations
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface QuizQuestion {
  id: string;
  question_text: string;
  question?: string;
  options: string[];
  correct_answer: any; // Can be number or object with selected_option
  explanation?: string;
}

interface ContentBlock {
  id: number;
  block_type: string;
  title: string;
  content: any;
  order: number;
  is_required: boolean;
  passing_score?: number;
  questions?: QuizQuestion[];
}

/** Prefer API questions; normalize legacy content.questions that use `question` not `question_text`. */
function resolveBlockQuestions(block: ContentBlock): QuizQuestion[] {
  const directQuestions = block.questions || [];
  const contentQuestions: QuizQuestion[] = block.content?.questions || [];
  const source = directQuestions.length > 0 ? directQuestions : contentQuestions;

  return source.map((q, index) => ({
    ...q,
    id: String(q.id ?? `q-${index}`),
    question_text: (q.question_text || q.question || '').trim(),
    options: Array.isArray(q.options) ? q.options : [],
  }));
}

interface TrainingSection {
  id: number;
  title: string;
  description: string;
  order: number;
  is_required: boolean;
  content_blocks: ContentBlock[];
}

interface Training {
  id: number;
  title: string;
  description: string;
  training_type: string;
  sections: TrainingSection[];
}

interface TrainingPlayerProps {
  training: Training;
  assignmentId?: number;
  assignmentCompletion?: any;
  onComplete: (results: any) => void;
  onClose: () => void;
  isReviewMode?: boolean; // New prop for review mode
  savedAnswers?: Record<string, number>; // Saved quiz answers for review mode
  savedQuizResults?: Record<string, boolean>; // Saved quiz results for review mode
  savedTestResults?: Record<string, boolean>; // Saved test results for review mode
  savedTestPassed?: boolean; // Saved test pass status
  savedTestScore?: number; // Saved test score
}

const TrainingPlayer: React.FC<TrainingPlayerProps> = ({ 
  training, 
  assignmentId,
  assignmentCompletion,
  onComplete, 
  onClose, 
  isReviewMode = false,
  savedAnswers = {},
  savedQuizResults = {},
  savedTestResults = {},
  savedTestPassed = false,
  savedTestScore = 0
}) => {
  const { user } = useAuth();
  const [progressSaveError, setProgressSaveError] = useState<string | null>(null);
  // Component mount/unmount tracking
  const mountIdRef = React.useRef(Math.random().toString(36).substr(2, 9));
  React.useEffect(() => {
    console.log('🟢 COMPONENT MOUNTED - ID:', mountIdRef.current, 'Training ID:', training?.id);
    return () => {
      console.log('🔴 COMPONENT UNMOUNTING - ID:', mountIdRef.current, 'Training ID:', training?.id);
    };
  }, []);
  
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  
  // Track state changes
  React.useEffect(() => {
    console.log('📍 Navigation changed - Mount ID:', mountIdRef.current, 'Section:', currentSectionIndex, 'Block:', currentBlockIndex);
  }, [currentSectionIndex, currentBlockIndex]);
  
  // Track training prop changes
  React.useEffect(() => {
    console.log('🔄 Training prop changed - Mount ID:', mountIdRef.current, 'Training ID:', training?.id, 'Training object:', training);
  }, [training]);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, number>>({});
  const [quizResults, setQuizResults] = useState<Record<string, boolean>>({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [completedBlocks, setCompletedBlocks] = useState<Set<number>>(new Set());
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [linkCertifications, setLinkCertifications] = useState<Record<number, boolean>>({});
  
  // Separate tracking for quiz vs test results
  const [testResults, setTestResults] = useState<Record<string, boolean>>({});
  const [testPassed, setTestPassed] = useState<boolean | null>(null);
  const [testScore, setTestScore] = useState<number>(0);

  // Video progress tracking
  const [videoProgress, setVideoProgress] = useState<{
    currentTime: number;
    duration: number;
    isPlaying: boolean;
  }>({ currentTime: 0, duration: 0, isPlaying: false });
  const [videoPlayerReady, setVideoPlayerReady] = useState(false); // Track when player is ready
  const [playbackRate, setPlaybackRate] = useState(1); // Current playback speed
  const videoPlayerRef = React.useRef<any>(null);
  const playerInitializedRef = React.useRef(false);
  const lastTrainingIdRef = React.useRef<number | null>(null);

  // Text-to-speech state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechRate, setSpeechRate] = useState(2); // Speech playback rate (default 2x)
  const currentUtteranceRef = React.useRef<SpeechSynthesisUtterance | null>(null);
  const currentTextRef = React.useRef<string>('');
  const isSpeakingRef = React.useRef<boolean>(false);

  // Progress tracking state
  const [assignmentCompletionId, setAssignmentCompletionId] = useState<number | null>(null);
  const [videoCompletions, setVideoCompletions] = useState<Record<string, {watched: boolean, last_position: number}>>({});
  const [trainingImageBlobUrl, setTrainingImageBlobUrl] = useState<string | null>(null);
  const [trainingImageError, setTrainingImageError] = useState(false);
  const [trainingImageLoading, setTrainingImageLoading] = useState(false);
  const trainingImageBlobRef = React.useRef<string | null>(null);

  // Check if speech synthesis is supported
  useEffect(() => {
    if ('speechSynthesis' in window) {
      setSpeechSupported(true);
    }
    
    // Cleanup speech on unmount
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  React.useEffect(() => {
    return () => {
      revokeTrainingImageBlobUrl(trainingImageBlobRef.current);
      trainingImageBlobRef.current = null;
    };
  }, []);

  // Initial setup effect - only runs once when training loads
  useEffect(() => {
    const currentTrainingId = training?.id;
    
    if (lastTrainingIdRef.current === currentTrainingId) {
      console.log('Initial setup already done for training ID:', currentTrainingId);
      return;
    }
    
    console.log('Running initial setup for training ID:', currentTrainingId);
    lastTrainingIdRef.current = currentTrainingId;
    setStartTime(new Date());
    
    // Find first section with content blocks
    if (training?.sections && training.sections.length > 0) {
      let firstValidSectionIndex = 0;
      while (firstValidSectionIndex < training.sections.length && 
             (!training.sections[firstValidSectionIndex]?.content_blocks || 
              training.sections[firstValidSectionIndex].content_blocks.length === 0)) {
        firstValidSectionIndex++;
      }
      
      if (firstValidSectionIndex < training.sections.length) {
        console.log('Setting initial position to section', firstValidSectionIndex, 'block 0');
        setCurrentSectionIndex(firstValidSectionIndex);
        setCurrentBlockIndex(0);
      }
    }
    
    // In review mode, show quiz results immediately and mark all blocks as completed
    if (isReviewMode) {
      setShowQuizResults(true);
      // Load saved answers and results
      setQuizAnswers(savedAnswers);
      setQuizResults(savedQuizResults);
      setTestResults(savedTestResults);
      setTestPassed(savedTestPassed);
      setTestScore(savedTestScore);
      // Mark all blocks as completed for review mode
      const allBlockIds = new Set<number>();
      training?.sections?.forEach(section => {
        section.content_blocks?.forEach(block => {
          allBlockIds.add(block.id);
        });
      });
      setCompletedBlocks(allBlockIds);
    }
  }, [training?.id, isReviewMode]); // Only depend on training ID, not the entire training object

  // Load YouTube IFrame API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    }
  }, []);

  // YouTube player initialization
  useEffect(() => {
    console.log('YouTube player effect running', { currentSectionIndex, currentBlockIndex });
    const currentBlock = training?.sections?.[currentSectionIndex]?.content_blocks?.[currentBlockIndex];
    const videoUrl = currentBlock?.content?.video_url;
    
    let player: any = null;
    
    if (currentBlock?.block_type === 'video' && videoUrl && window.YT && window.YT.Player) {
      console.log('Initializing YouTube player for block', currentBlock.id);
      const embedUrl = convertToEmbedUrl(videoUrl);
      const getVideoId = (url: string) => {
        const match = url.match(/(?:youtube\.com\/embed\/|youtu\.be\/)([^?&]+)/);
        return match ? match[1] : null;
      };

      const videoId = embedUrl ? getVideoId(embedUrl) : null;

      if (videoId) {
        const playerId = `youtube-player-${currentBlock.id}`;
        const existingPlayer = document.getElementById(playerId);
        console.log('Player div exists:', !!existingPlayer, 'Player initialized:', playerInitializedRef.current);

        // Always try to create the player if the div exists, regardless of previous initialization
        if (existingPlayer) {
          console.log('Creating YouTube player for', videoId);
          playerInitializedRef.current = true;
          player = new window.YT.Player(playerId, {
            videoId: videoId,
            playerVars: {
              autoplay: 0,
              controls: 0,
              disablekb: 1,
              fs: 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0,
              enablejsapi: 1
            },
            events: {
              onReady: (event: any) => {
                console.log('🎥 YouTube player ready - Mount ID:', mountIdRef.current, 'Current position:', currentSectionIndex, currentBlockIndex);
                event.target.lastValidTime = 0;
                videoPlayerRef.current = event.target;
                setVideoPlayerReady(true); // Trigger progress tracking to start

                // Set initial playback rate
                if (typeof event.target.setPlaybackRate === 'function') {
                  event.target.setPlaybackRate(playbackRate);
                }
              },
              onStateChange: (event: any) => {
                if (event.data === window.YT.PlayerState.PLAYING) {
                  const interval = setInterval(() => {
                    const currentTime = event.target.getCurrentTime();
                    const lastValidTime = event.target.lastValidTime || 0;
                    
                    if (currentTime > lastValidTime + 2) {
                      event.target.seekTo(lastValidTime, true);
                    } else {
                      event.target.lastValidTime = currentTime;
                    }
                  }, 500);
                  
                  event.target.progressInterval = interval;
                } else if (event.data === window.YT.PlayerState.PAUSED) {
                  if (event.target.progressInterval) {
                    clearInterval(event.target.progressInterval);
                  }
                }
              }
            }
          });
        }
      }
    }
    
    // Cleanup function
    return () => {
      if (player && typeof player.destroy === 'function') {
        try {
          player.destroy();
        } catch (error) {
          console.error('Error destroying YouTube player:', error);
        }
      }
    };
  }, [currentSectionIndex, currentBlockIndex, training]);

  // Video progress tracking
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    const currentBlockType = training?.sections?.[currentSectionIndex]?.content_blocks?.[currentBlockIndex]?.block_type;
    const currentBlockId = training?.sections?.[currentSectionIndex]?.content_blocks?.[currentBlockIndex]?.id;
    
    console.log('🎬 Video progress effect:', { currentBlockType, hasPlayer: !!videoPlayerRef.current, playerReady: videoPlayerReady });
    
    if (currentBlockType === 'video' && videoPlayerRef.current && videoPlayerReady && currentBlockId) {
      console.log('▶️ Starting video progress tracking');
      interval = setInterval(() => {
        try {
          const currentTime = videoPlayerRef.current.getCurrentTime();
          const duration = videoPlayerRef.current.getDuration();
          const playbackRate = videoPlayerRef.current.getPlaybackRate();
          const remainingTime = (duration - currentTime) / playbackRate;
          console.log('📊 Video progress:', { currentTime, duration, playbackRate, remaining: remainingTime });
          setVideoProgress({ currentTime, duration, isPlaying: true });
          
          // Check if video has reached the end (less than 30 seconds remaining)
          if (duration > 0 && (duration - currentTime) <= 30) {
            const blockIdStr = String(currentBlockId);
            if (!videoCompletions[blockIdStr]?.watched) {
              console.log('✅ Video completed! Marking as watched:', blockIdStr);
              
              const newVideoCompletions = {
                ...videoCompletions,
                [blockIdStr]: { watched: true, last_position: duration }
              };
              setVideoCompletions(newVideoCompletions);
              
              // Mark block as completed
              const newCompletedBlocks = new Set([...Array.from(completedBlocks), currentBlockId]);
              setCompletedBlocks(newCompletedBlocks);
              
              // Save progress
              saveProgressDebounced.current?.({
                current_section_index: currentSectionIndex,
                current_block_index: currentBlockIndex,
                completed_blocks: Array.from(newCompletedBlocks),
                video_completions: newVideoCompletions
              });
            }
          }
        } catch (error) {
          console.error('Error getting video progress:', error);
        }
      }, 1000); // Check every second
    } else if (currentBlockType !== 'video') {
      // Only reset if we're NOT on a video block
      console.log('⏹️ Resetting video progress (not on video block)');
      setVideoProgress({ currentTime: 0, duration: 0, isPlaying: false });
      setVideoPlayerReady(false); // Reset player ready state
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentSectionIndex, currentBlockIndex, training, videoPlayerReady, videoCompletions, completedBlocks]);

  // Debug effect - runs when navigation changes
  useEffect(() => {
    console.log('TrainingPlayer Debug:', {
      training,
      sections: training?.sections,
      currentSectionIndex,
      currentBlockIndex,
      currentSection: training?.sections?.[currentSectionIndex],
      currentBlock: training?.sections?.[currentSectionIndex]?.content_blocks?.[currentBlockIndex]
    });
  }, [training, currentSectionIndex, currentBlockIndex]);

  // Load saved progress when training starts
  useEffect(() => {
    const loadSavedProgress = async () => {
      if (!training?.id || !assignmentId || isReviewMode) return;
      
      try {
        console.log('📥 Loading saved progress for assignment:', assignmentId);
        
        const belongsToCurrentUser = (completion: any) => {
          if (!user?.id || !completion) return false;
          const assigneeId = completion.assignee?.id ?? completion.assignee_id ?? completion.assignee;
          return Number(assigneeId) === Number(user.id);
        };

        // Never restore another assignee's row. A rejected save must not display their progress.
        let completionData = belongsToCurrentUser(assignmentCompletion) ? assignmentCompletion : null;

        if (!completionData) {
          const response = await api.get(
            `/assignment-completions/?assignment=${assignmentId}&assignee=${user?.id || ''}`
          );
          const results = response.data.results || [];
          completionData = results.find(belongsToCurrentUser) || null;
        }
        
        if (completionData) {
          setAssignmentCompletionId(completionData.id);
          
          const progress = completionData.completion_data || {};
          console.log('📥 Loaded progress data:', progress);
          
          // Restore position
          if (progress.current_section_index !== undefined) {
            setCurrentSectionIndex(progress.current_section_index);
          }
          if (progress.current_block_index !== undefined) {
            setCurrentBlockIndex(progress.current_block_index);
          }
          
          // Restore completed blocks
          if (progress.completed_blocks && Array.isArray(progress.completed_blocks)) {
            setCompletedBlocks(new Set(progress.completed_blocks));
          }
          
          // Restore video completions
          if (progress.video_completions) {
            setVideoCompletions(progress.video_completions);
          }
          
          // Restore quiz data
          if (progress.quiz_answers) {
            setQuizAnswers(progress.quiz_answers);
          }
          if (progress.quiz_results) {
            setQuizResults(progress.quiz_results);
          }
          
          console.log('✅ Progress restored successfully');
        }
      } catch (error) {
        console.error('❌ Error loading saved progress:', error);
      }
    };
    
    loadSavedProgress();
  }, [training?.id, assignmentId, isReviewMode, assignmentCompletion, user?.id]);

  const saveProgressNow = async (progressData: any): Promise<boolean> => {
    if (isReviewMode) return true;
    if (!assignmentCompletionId) {
      setProgressSaveError('Progress was not saved for your assignment.');
      return false;
    }
    try {
      await api.patch(`/assignment-completions/${assignmentCompletionId}/update_progress/`, {
        progress: progressData,
      });
      setProgressSaveError(null);
      return true;
    } catch (error) {
      console.error('Failed to save progress:', error);
      setProgressSaveError('Progress was not saved. This assignment was not updated.');
      return false;
    }
  };

  // Debounced save progress function
  const saveProgressDebounced = React.useRef<(progressData: any) => void>();
  
  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    saveProgressDebounced.current = (progressData: any) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      timeoutId = setTimeout(async () => {
        if (!assignmentCompletionId || isReviewMode) return;
        
        try {
          console.log('💾 Saving progress:', progressData);
          await api.patch(`/assignment-completions/${assignmentCompletionId}/update_progress/`, {
            progress: progressData
          });
          console.log('✅ Progress saved successfully');
        } catch (error) {
          console.error('❌ Failed to save progress:', error);
        }
      }, 1000);
    };
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [assignmentCompletionId, isReviewMode]);

  const imageBlockForEffect =
    training?.sections?.[currentSectionIndex]?.content_blocks?.[currentBlockIndex];

  useEffect(() => {
    if (!imageBlockForEffect || imageBlockForEffect.block_type !== 'image') {
      revokeTrainingImageBlobUrl(trainingImageBlobRef.current);
      trainingImageBlobRef.current = null;
      setTrainingImageBlobUrl(null);
      setTrainingImageError(false);
      setTrainingImageLoading(false);
      return;
    }

    const imageUrl = imageBlockForEffect.content?.image_url;
    const storagePath = imageBlockForEffect.content?.storage_path;
    if (!imageUrl && !storagePath) {
      revokeTrainingImageBlobUrl(trainingImageBlobRef.current);
      trainingImageBlobRef.current = null;
      setTrainingImageBlobUrl(null);
      setTrainingImageError(false);
      setTrainingImageLoading(false);
      return;
    }

    if (isExternalImageUrl(imageUrl) && !storagePath) {
      revokeTrainingImageBlobUrl(trainingImageBlobRef.current);
      trainingImageBlobRef.current = null;
      setTrainingImageBlobUrl(null);
      setTrainingImageError(false);
      setTrainingImageLoading(false);
      return;
    }

    if (!shouldLoadTrainingImageViaApi(imageBlockForEffect.id, imageUrl, storagePath)) {
      revokeTrainingImageBlobUrl(trainingImageBlobRef.current);
      trainingImageBlobRef.current = null;
      setTrainingImageBlobUrl(null);
      setTrainingImageError(false);
      setTrainingImageLoading(false);
      return;
    }

    let active = true;

    setTrainingImageError(false);
    setTrainingImageLoading(true);
    revokeTrainingImageBlobUrl(trainingImageBlobRef.current);
    trainingImageBlobRef.current = null;
    setTrainingImageBlobUrl(null);

    fetchTrainingImageBlobUrl(imageBlockForEffect.id)
      .then((url) => {
        if (!active) {
          revokeTrainingImageBlobUrl(url);
          return;
        }
        revokeTrainingImageBlobUrl(trainingImageBlobRef.current);
        trainingImageBlobRef.current = url;
        setTrainingImageBlobUrl(url);
      })
      .catch(() => {
        if (active) {
          setTrainingImageError(true);
        }
      })
      .finally(() => {
        if (active) {
          setTrainingImageLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    imageBlockForEffect?.id,
    imageBlockForEffect?.block_type,
    imageBlockForEffect?.content?.image_url,
    imageBlockForEffect?.content?.storage_path,
  ]);

  // Add safety checks for training data
  if (!training || !training.sections || !Array.isArray(training.sections) || training.sections.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Training content is not available or is incomplete.
        </Alert>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </Box>
    );
  }

  const currentSection = training.sections[currentSectionIndex];
  const currentBlock = currentSection?.content_blocks?.[currentBlockIndex];
  const totalSections = training.sections.length;
  const totalBlocks = training.sections.reduce((sum, section) => sum + (section.content_blocks?.length || 0), 0);

  // Calculate progress
  const completedBlocksCount = completedBlocks.size;
  const progressPercentage = totalBlocks > 0 ? (completedBlocksCount / totalBlocks) * 100 : 0;

  const handleNext = (updatedCompletedBlocks?: Set<number>) => {
    // Stop any ongoing speech when navigating
    stopSpeaking();
    
    // Use the provided completed blocks or the current state
    const blocksToSave = updatedCompletedBlocks || completedBlocks;
    
    console.log('handleNext called:', {
      currentBlockIndex,
      contentBlocksLength: currentSection?.content_blocks?.length || 0,
      currentSectionIndex,
      totalSections: training.sections.length,
      condition1: currentBlockIndex < (currentSection?.content_blocks?.length || 0) - 1,
      condition2: currentSectionIndex < training.sections.length - 1,
      completedBlocksCount: blocksToSave.size
    });
    
    if (currentBlockIndex < (currentSection?.content_blocks?.length || 0) - 1) {
      // More blocks in current section
      console.log('Moving to next block in current section');
      const newBlockIndex = currentBlockIndex + 1;
      setCurrentBlockIndex(newBlockIndex);
      
      // Save progress with NEW position
      if (!isReviewMode) {
        saveProgressDebounced.current?.({
          current_section_index: currentSectionIndex,
          current_block_index: newBlockIndex,
          completed_blocks: Array.from(blocksToSave),
          video_completions: videoCompletions,
          quiz_answers: quizAnswers,
          quiz_results: quizResults
        });
      }
    } else if (currentSectionIndex < training.sections.length - 1) {
      // Move to next section
      console.log('Moving to next section');
      let nextSectionIndex = currentSectionIndex + 1;
      
      // Skip sections without content blocks
      while (nextSectionIndex < training.sections.length && 
             (!training.sections[nextSectionIndex]?.content_blocks || 
              training.sections[nextSectionIndex].content_blocks.length === 0)) {
        console.log(`Skipping section ${nextSectionIndex} (no content blocks)`);
        nextSectionIndex++;
      }
      
      if (nextSectionIndex < training.sections.length) {
        console.log(`Moving to section ${nextSectionIndex}, block 0`);
        setCurrentSectionIndex(nextSectionIndex);
        setCurrentBlockIndex(0);
        
        // Save progress with NEW position
        if (!isReviewMode) {
          saveProgressDebounced.current?.({
            current_section_index: nextSectionIndex,
            current_block_index: 0,
            completed_blocks: Array.from(blocksToSave),
            video_completions: videoCompletions,
            quiz_answers: quizAnswers,
            quiz_results: quizResults
          });
        }
      } else {
        // No more sections with content blocks, training completed
        if (!isReviewMode) {
          const endTime = new Date();
          const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60); // minutes
          
          // Calculate quiz score for reporting (doesn't affect overall score)
          const quizScore = Object.keys(quizResults).length > 0 
            ? Math.round((Object.values(quizResults).filter(Boolean).length / Object.keys(quizResults).length) * 100)
            : null;
          
          onComplete({
            trainingId: training.id,
            completedAt: endTime,
            duration: duration,
            quizResults: quizResults,
            testResults: testResults,
            testPassed: testPassed,
            testScore: testScore, // Primary scoring mechanism
            quizScore: quizScore, // For reporting only
            totalQuestions: Object.keys(quizAnswers).length,
            correctAnswers: Object.values(quizResults).filter(Boolean).length,
            testQuestions: Object.keys(testResults).length,
            testCorrectAnswers: Object.values(testResults).filter(Boolean).length
          });
        } else {
          // In review mode, just go back to the beginning
          let firstValidSectionIndex = 0;
          while (firstValidSectionIndex < training.sections.length && 
                 (!training.sections[firstValidSectionIndex]?.content_blocks || 
                  training.sections[firstValidSectionIndex].content_blocks.length === 0)) {
            firstValidSectionIndex++;
          }
          
          if (firstValidSectionIndex < training.sections.length) {
            setCurrentSectionIndex(firstValidSectionIndex);
            setCurrentBlockIndex(0);
          }
        }
      }
    } else {
      // Training completed
      if (!isReviewMode) {
        const endTime = new Date();
        const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60); // minutes
        
        // Calculate quiz score for reporting (doesn't affect overall score)
        const quizScore = Object.keys(quizResults).length > 0 
          ? Math.round((Object.values(quizResults).filter(Boolean).length / Object.keys(quizResults).length) * 100)
          : null;
        
        onComplete({
          trainingId: training.id,
          completedAt: endTime,
          duration: duration,
          quizResults: quizResults,
          testResults: testResults,
          testPassed: testPassed,
          testScore: testScore, // Primary scoring mechanism
          quizScore: quizScore, // For reporting only
          totalQuestions: Object.keys(quizAnswers).length,
          correctAnswers: Object.values(quizResults).filter(Boolean).length,
          testQuestions: Object.keys(testResults).length,
          testCorrectAnswers: Object.values(testResults).filter(Boolean).length
        });
      } else {
        // In review mode, just go back to the beginning
        let firstValidSectionIndex = 0;
        while (firstValidSectionIndex < training.sections.length && 
               (!training.sections[firstValidSectionIndex]?.content_blocks || 
                training.sections[firstValidSectionIndex].content_blocks.length === 0)) {
          firstValidSectionIndex++;
        }
        
        if (firstValidSectionIndex < training.sections.length) {
          setCurrentSectionIndex(firstValidSectionIndex);
          setCurrentBlockIndex(0);
        }
      }
    }
  };

  const canNavigateToBlock = (sectionIndex: number, blockIndex: number): boolean => {
    const blocks = training.sections[sectionIndex]?.content_blocks;
    if (!blocks || blockIndex < 0 || blockIndex >= blocks.length) {
      return false;
    }

    if (isReviewMode) {
      return true;
    }

    const block = blocks[blockIndex];
    if (completedBlocks.has(block.id)) {
      return true;
    }

    if (sectionIndex < currentSectionIndex) {
      return true;
    }

    if (sectionIndex === currentSectionIndex && blockIndex < currentBlockIndex) {
      return true;
    }

    return false;
  };

  const getFirstNavigableBlockIndex = (sectionIndex: number): number | null => {
    const blocks = training.sections[sectionIndex]?.content_blocks || [];
    for (let i = 0; i < blocks.length; i++) {
      if (canNavigateToBlock(sectionIndex, i)) {
        return i;
      }
    }
    return null;
  };

  const navigateToBlock = (sectionIndex: number, blockIndex: number) => {
    if (!canNavigateToBlock(sectionIndex, blockIndex)) {
      return;
    }

    stopSpeaking();
    setCurrentSectionIndex(sectionIndex);
    setCurrentBlockIndex(blockIndex);

    if (!isReviewMode) {
      saveProgressDebounced.current?.({
        current_section_index: sectionIndex,
        current_block_index: blockIndex,
        completed_blocks: Array.from(completedBlocks),
        video_completions: videoCompletions,
        quiz_answers: quizAnswers,
        quiz_results: quizResults,
      });
    }
  };

  const handlePrevious = () => {
    // Stop any ongoing speech when navigating
    stopSpeaking();
    
    if (currentBlockIndex > 0) {
      setCurrentBlockIndex(currentBlockIndex - 1);
    } else if (currentSectionIndex > 0) {
      // Move to previous section
      let prevSectionIndex = currentSectionIndex - 1;
      
      // Skip sections without content blocks
      while (prevSectionIndex >= 0 && 
             (!training.sections[prevSectionIndex]?.content_blocks || 
              training.sections[prevSectionIndex].content_blocks.length === 0)) {
        prevSectionIndex--;
      }
      
      if (prevSectionIndex >= 0) {
        setCurrentSectionIndex(prevSectionIndex);
        const prevSection = training.sections[prevSectionIndex];
        setCurrentBlockIndex((prevSection?.content_blocks?.length || 0) - 1);
      }
    }
  };

  const handleQuizAnswer = (questionId: string, answerIndex: number) => {
    setQuizAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: answerIndex };
      return newAnswers;
    });
  };

  const handleQuizSubmit = () => {
    const results: Record<string, boolean> = {};
    const questions = resolveBlockQuestions(currentBlock);
    
    Object.keys(quizAnswers).forEach(questionId => {
      // Convert both to strings for comparison to handle type mismatch
      const question = questions.find((q: QuizQuestion) => String(q.id) === questionId);
      if (question) {
        const correctAnswerIndex = typeof question.correct_answer === 'object' 
          ? question.correct_answer?.selected_option 
          : question.correct_answer;
        const isCorrect = quizAnswers[questionId] === correctAnswerIndex;
        results[questionId] = isCorrect;
      }
    });
    
    // Handle differently for test blocks vs quiz blocks
    if (currentBlock.block_type === 'test') {
      // TEST BLOCKS: Primary scoring mechanism for training completion
      const correctCount = Object.values(results).filter(Boolean).length;
      const totalQuestions = questions.length;
      const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
      const passingScore = currentBlock.passing_score || 80;
      const passed = score >= passingScore;
      
      setTestResults(results);
      setTestScore(score);
      setTestPassed(passed);
      setShowQuizResults(true);
    } else {
      // QUIZ BLOCKS: For user feedback only, does not affect training score
      setQuizResults(results);
      setShowQuizResults(true);
    }
  };

  const handleQuizContinue = () => {
    setShowQuizResults(false);
    
    // Mark quiz block as completed
    let newCompletedBlocks = completedBlocks;
    if (currentBlock?.id && !completedBlocks.has(currentBlock.id)) {
      newCompletedBlocks = new Set([...Array.from(completedBlocks), currentBlock.id]);
      setCompletedBlocks(newCompletedBlocks);
    }
    
    handleNext(newCompletedBlocks);
  };

  const handleBlockComplete = async () => {
    console.log('handleBlockComplete called', {
      currentSectionIndex,
      currentBlockIndex,
      blockType: currentBlock?.block_type,
      videoProgress
    });
    
    // For video blocks, check if video is in videoCompletions as watched
    const isVideoWatched = currentBlock?.block_type === 'video' 
      ? videoCompletions[String(currentBlock.id)]?.watched 
      : true;
    
    // Check if button should be disabled
    const isDisabled = (currentBlock?.block_type === 'link' &&
                       currentBlock?.content?.requires_certification &&
                       !linkCertifications[currentBlock.id]) ||
                      (currentBlock?.block_type === 'video' &&
                       !isVideoWatched &&
                       (videoProgress.duration === 0 || // Video not loaded/started yet
                        (videoProgress.duration - videoProgress.currentTime) > 30)); // Or more than 30 seconds remaining (video time, not real time)

    console.log('Button disabled?', isDisabled);
    
    if (isDisabled) {
      console.log('Button is disabled, not proceeding');
      return; // Don't proceed if button should be disabled
    }
    
    console.log('Proceeding to next block');
    // Mark current block as completed only after the signed-in assignee's save succeeds.
    let newCompletedBlocks = completedBlocks;
    if (currentBlock?.id && !completedBlocks.has(currentBlock.id)) {
      newCompletedBlocks = new Set([...Array.from(completedBlocks), currentBlock.id]);
    }

    if (!isReviewMode) {
      const saved = await saveProgressNow({
        current_section_index: currentSectionIndex,
        current_block_index: currentBlockIndex,
        completed_blocks: Array.from(newCompletedBlocks),
        video_completions: videoCompletions,
        quiz_answers: quizAnswers,
        quiz_results: quizResults,
      });
      if (!saved) {
        return;
      }
    }

    setCompletedBlocks(newCompletedBlocks);
    handleNext(newCompletedBlocks);
  };

  const handleLinkCertification = (blockId: number, certified: boolean) => {
    setLinkCertifications(prev => ({ ...prev, [blockId]: certified }));
  };

  const handlePlaybackRateChange = (rate: number) => {
    if (videoPlayerRef.current && typeof videoPlayerRef.current.setPlaybackRate === 'function') {
      try {
        videoPlayerRef.current.setPlaybackRate(rate);
        setPlaybackRate(rate);
        console.log('Playback rate changed to:', rate);
      } catch (error) {
        console.error('Error setting playback rate:', error);
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const speakText = (text: string) => {
    if (!speechSupported) {
      console.warn('Speech synthesis not supported in this browser');
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    // Store the text
    currentTextRef.current = text;
    isSpeakingRef.current = true;
    setIsSpeaking(true);
    setIsPaused(false);
    
    // Create utterance for entire text (no chunking needed)
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = speechRate;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    currentUtteranceRef.current = utterance;
    
    utterance.onend = () => {
      // Speech completed
      setIsSpeaking(false);
      setIsPaused(false);
      isSpeakingRef.current = false;
      currentUtteranceRef.current = null;
      currentTextRef.current = '';
    };
    
    utterance.onerror = (event) => {
      console.error('Speech error:', event);
      stopSpeaking();
    };
    
    console.log(`Starting speech at ${speechRate}x speed`);
    window.speechSynthesis.speak(utterance);
  };

  const pauseSpeaking = () => {
    if ('speechSynthesis' in window) {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.pause();
        setIsPaused(true);
      }
    }
  };

  const resumeSpeaking = () => {
    if ('speechSynthesis' in window) {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
        setIsPaused(false);
      } else if (!window.speechSynthesis.speaking && isSpeakingRef.current && currentTextRef.current) {
        // If we're supposed to be speaking but nothing is active (e.g., paused at end of chunk),
        // restart from the stored text
        const utterance = new SpeechSynthesisUtterance(currentTextRef.current);
        utterance.rate = speechRate;
        utterance.pitch = 1;
        utterance.volume = 1;
        
        currentUtteranceRef.current = utterance;
        
        utterance.onend = () => {
          setIsSpeaking(false);
          setIsPaused(false);
          isSpeakingRef.current = false;
          currentUtteranceRef.current = null;
          currentTextRef.current = '';
        };
        
        utterance.onerror = (event) => {
          console.error('Speech error:', event);
          stopSpeaking();
        };
        
        window.speechSynthesis.speak(utterance);
        setIsPaused(false);
      }
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
      isSpeakingRef.current = false;
      currentUtteranceRef.current = null;
      currentTextRef.current = '';
    }
  };

  const getBlockIcon = (blockType: string) => {
    switch (blockType) {
      case 'quiz': return <QuizIcon />;
      case 'video': return <VideoIcon />;
      case 'text': return <TextIcon />;
      case 'link': return <LinkIcon />;
      case 'image': return <ImageIcon />;
      case 'document': return <DocumentIcon />;
      default: return <TextIcon />;
    }
  };

  const convertToEmbedUrl = (url: string | undefined | null) => {
    // Handle undefined/null URLs
    if (!url || typeof url !== 'string') {
      return '';
    }
    
    // Handle YouTube URLs
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }
    // Handle YouTube embed URLs (already correct format)
    if (url.includes('youtube.com/embed/')) {
      return url;
    }
    // For other video URLs, return as-is
    return url;
  };

  // Handle sections without content blocks
  if (!currentSection?.content_blocks || currentSection.content_blocks.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Section "{currentSection?.title}" has no content blocks.
        </Alert>
        <Button variant="contained" onClick={() => handleNext()}>
          Continue to Next Section
        </Button>
        <Button variant="outlined" onClick={onClose} sx={{ ml: 2 }}>
          Close Training
        </Button>
      </Box>
    );
  }

  // Add additional safety check for currentBlock
  if (!currentBlock) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Current content block is not available. Please refresh the page.
        </Alert>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </Box>
    );
  }

  const renderContentBlock = () => {
    if (!currentBlock) return null;

    switch (currentBlock.block_type) {
      case 'text':
        const textContent = currentBlock.content?.text || '';
        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                {currentBlock.title}
              </Typography>
                {speechSupported && textContent && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {!isSpeaking ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VolumeUpIcon />}
                        onClick={() => speakText(textContent)}
                      >
                        Read Aloud
                      </Button>
                    ) : isPaused ? (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={resumeSpeaking}
                          color="primary"
                        >
                          Resume
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<StopIcon />}
                          onClick={stopSpeaking}
                          color="secondary"
                        >
                          Stop
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<PauseIcon />}
                          onClick={pauseSpeaking}
                          color="primary"
                        >
                          Pause
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<StopIcon />}
                          onClick={stopSpeaking}
                          color="secondary"
                        >
                          Stop
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </Box>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {textContent}
              </Typography>
            </CardContent>
          </Card>
        );

      case 'video':
        const videoUrl = convertToEmbedUrl(currentBlock.content?.video_url);
        
        // Extract video ID from URL for YouTube API
        const getVideoId = (url: string) => {
          const match = url.match(/(?:youtube\.com\/embed\/|youtu\.be\/)([^?&]+)/);
          return match ? match[1] : null;
        };
        
        const videoId = videoUrl ? getVideoId(videoUrl) : null;
        
        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentBlock.content?.video_title || currentBlock.title}
              </Typography>
              {videoId && window.YT && window.YT.Player ? (
                <>
                  <Box sx={{ 
                    position: 'relative', 
                    paddingTop: '56.25%', 
                    mb: 2,
                    '& iframe': {
                      opacity: 0.99
                    }
                  }}>
                    <div
                      id={`youtube-player-${currentBlock.id}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  </Box>
                  
                  {/* Custom Video Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <IconButton 
                      onClick={() => {
                        try {
                          if (videoPlayerRef.current && typeof videoPlayerRef.current.playVideo === 'function') {
                            videoPlayerRef.current.playVideo();
                          }
                        } catch (error) {
                          console.error('Error playing video:', error);
                        }
                      }}
                      disabled={!videoPlayerRef.current || !videoPlayerReady}
                      size="small"
                      color="primary"
                    >
                      <PlayArrowIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => {
                        try {
                          if (videoPlayerRef.current && typeof videoPlayerRef.current.pauseVideo === 'function') {
                            videoPlayerRef.current.pauseVideo();
                          }
                        } catch (error) {
                          console.error('Error pausing video:', error);
                        }
                      }}
                      disabled={!videoPlayerRef.current || !videoPlayerReady}
                      size="small"
                      color="primary"
                    >
                      <PauseIcon />
                    </IconButton>
                    <LinearProgress 
                      variant="determinate" 
                      value={videoProgress.duration > 0 ? (videoProgress.currentTime / videoProgress.duration) * 100 : 0}
                      sx={{ flex: 1, height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'right' }}>
                      {formatTime(videoProgress.currentTime)} / {formatTime(videoProgress.duration)}
                    </Typography>
                  </Box>

                  {/* Playback Speed Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ minWidth: 30 }}>
                      {playbackRate}x
                    </Typography>
                    {[0.75, 1, 1.25, 1.5, 1.75, 2].map((speed) => (
                      <Chip
                        key={speed}
                        label={`${speed}x`}
                        onClick={() => handlePlaybackRateChange(speed)}
                        color={playbackRate === speed ? 'primary' : 'default'}
                        size="small"
                        variant={playbackRate === speed ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>

                  {/* Progress indicator */}
                  {videoProgress.duration > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {(videoProgress.duration - videoProgress.currentTime) <= 30 ? (
                        <Chip
                          label="✓ You can proceed to next section"
                          color="success"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      ) : (
                        <Chip
                          label={`Watch ${Math.ceil(((videoProgress.duration - videoProgress.currentTime) - 30) / 60)} more minutes to proceed`}
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  )}
                </>
              ) : videoUrl ? (
                <Box sx={{ position: 'relative', paddingTop: '56.25%', mb: 2 }}>
                  <iframe
                    src={videoUrl}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0
                    }}
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    title={currentBlock.content?.video_title || currentBlock.title}
                  />
                </Box>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Video URL not available for this content block.
                </Alert>
              )}
              {currentBlock.content?.description && (
                <Typography variant="body2" color="text.secondary">
                  {currentBlock.content.description}
                </Typography>
              )}
            </CardContent>
          </Card>
        );

      case 'link':
        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentBlock.content?.link_title || currentBlock.title}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {currentBlock.content?.description || ''}
              </Typography>
              {currentBlock.content?.link_url ? (
                <Button
                  variant="contained"
                  href={currentBlock.content.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mr: 2 }}
                >
                  Open Link
                </Button>
              ) : (
                <Alert severity="warning" sx={{ mr: 2 }}>
                  Link URL not available for this content block.
                </Alert>
              )}
              {currentBlock.content?.requires_certification && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Please review the linked content and certify that you have read and understood it.
                  </Alert>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      id={`certify-${currentBlock.id}`}
                      checked={linkCertifications[currentBlock.id] || false}
                      onChange={(e) => handleLinkCertification(currentBlock.id, e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <label htmlFor={`certify-${currentBlock.id}`}>
                      <Typography variant="body2">
                        I certify that I have reviewed and understood the linked content above.
                      </Typography>
                    </label>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        );

      case 'image': {
        const imageUrl = currentBlock.content?.image_url;
        const storagePath = currentBlock.content?.storage_path;
        const loadViaApi = shouldLoadTrainingImageViaApi(
          currentBlock.id,
          imageUrl,
          storagePath
        );
        const directSrc =
          !loadViaApi && isExternalImageUrl(imageUrl) ? imageUrl : null;
        const displaySrc = loadViaApi ? trainingImageBlobUrl : directSrc;

        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentBlock.title}
              </Typography>
              {!imageUrl && !storagePath ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Image URL not available for this content block.
                </Alert>
              ) : trainingImageError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Unable to load this image. It may need to be re-uploaded.
                </Alert>
              ) : loadViaApi && trainingImageLoading ? (
                <Box sx={{ mb: 2 }}>
                  <LinearProgress />
                </Box>
              ) : displaySrc ? (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img
                    src={displaySrc}
                    alt={currentBlock.content?.caption || currentBlock.title}
                    style={{ maxWidth: '100%', height: 'auto' }}
                    onError={() => {
                      if (!loadViaApi) {
                        setTrainingImageError(true);
                      }
                    }}
                  />
                </Box>
              ) : loadViaApi ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Unable to load this image. It may need to be re-uploaded.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Image URL not available for this content block.
                </Alert>
              )}
              {currentBlock.content?.caption && (
                <Typography variant="body2" color="text.secondary" align="center">
                  {currentBlock.content.caption}
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      }

      case 'document':
        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentBlock.content?.file_name || currentBlock.title}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {currentBlock.content?.description || ''}
              </Typography>
              {currentBlock.content?.file_url ? (
                <Button
                  variant="contained"
                  href={currentBlock.content.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Document
                </Button>
              ) : (
                <Alert severity="warning">
                  Document URL not available for this content block.
                </Alert>
              )}
            </CardContent>
          </Card>
        );

      case 'quiz': {
        const questions = resolveBlockQuestions(currentBlock);

        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentBlock.content?.quiz_title || currentBlock.title}
              </Typography>
              
              {questions.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No quiz questions found for this content block.
                </Alert>
              )}
              
              {questions.map((question: QuizQuestion, questionIndex: number) => {
                return (
                <Box key={question.id} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Question {questionIndex + 1}: {question.question_text}
                  </Typography>
                  
                  <Box sx={{ ml: 2 }}>
                    {question.options.map((option, optionIndex) => {
                      // Handle correct_answer as either number or object with selected_option
                      const correctAnswerIndex = typeof question.correct_answer === 'object' 
                        ? question.correct_answer?.selected_option 
                        : question.correct_answer;
                      const isCorrect = optionIndex === correctAnswerIndex;
                      const isSelected = quizAnswers[question.id] === optionIndex;
                      const isWrong = showQuizResults && isSelected && !isCorrect;
                      
                      
                      return (
                        <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                          <input
                            type="radio"
                            name={`question-${currentBlock.id}-${question.id}`}
                            value={optionIndex}
                            checked={isSelected}
                            onChange={() => handleQuizAnswer(question.id, optionIndex)}
                            disabled={showQuizResults || isReviewMode}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              ml: 1,
                              fontWeight: showQuizResults && isCorrect ? 'bold' : 'normal',
                              color: showQuizResults 
                                ? isCorrect 
                                  ? 'success.main' 
                                  : isWrong 
                                    ? 'error.main' 
                                    : 'text.secondary'
                                : 'text.primary'
                            }}
                          >
                            {option}
                          </Typography>
                          {showQuizResults && (
                            <>
                              {isCorrect && <CheckIcon color="success" sx={{ ml: 1 }} />}
                              {isWrong && <ClearIcon color="error" sx={{ ml: 1 }} />}
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {showQuizResults && question.explanation && (
                    <Alert 
                      severity={quizResults[question.id] ? 'success' : 'error'} 
                      sx={{ mt: 1 }}
                    >
                      <strong>Explanation:</strong> {question.explanation}
                    </Alert>
                  )}
                </Box>
              );
              })}

              {!showQuizResults && !isReviewMode ? (
                <Button
                  variant="contained"
                  onClick={handleQuizSubmit}
                  disabled={Object.keys(quizAnswers).length < questions.length}
                >
                  Submit Quiz
                </Button>
              ) : showQuizResults ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Quiz completed! Review your answers and the explanations above.
                  </Alert>
                  <Button variant="contained" onClick={handleQuizContinue}>
                    Continue
                  </Button>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        );
      }

      case 'test': {
        const testQuestions = resolveBlockQuestions(currentBlock);

        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {currentBlock.title}
                </Typography>
                <Chip 
                  label="Final Assessment" 
                  color="primary" 
                  size="small"
                  icon={<QuizIcon />}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Passing Score: {currentBlock.passing_score || 80}%
              </Typography>
              
              {testQuestions.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No test questions found for this final assessment.
                </Alert>
              )}
              
              {testQuestions.map((question: QuizQuestion, questionIndex: number) => {
                return (
                <Box key={question.id} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Question {questionIndex + 1}: {question.question_text}
                  </Typography>
                  
                  <Box sx={{ ml: 2 }}>
                    {question.options.map((option, optionIndex) => {
                      // Handle correct_answer as either number or object with selected_option
                      const correctAnswerIndex = typeof question.correct_answer === 'object' 
                        ? question.correct_answer?.selected_option 
                        : question.correct_answer;
                      const isCorrect = optionIndex === correctAnswerIndex;
                      const isSelected = quizAnswers[question.id] === optionIndex;
                      const isWrong = showQuizResults && isSelected && !isCorrect;
                      
                      return (
                        <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <input
                            type="radio"
                            name={`question-${currentBlock.id}-${question.id}`}
                            value={optionIndex}
                            checked={isSelected}
                            onChange={() => handleQuizAnswer(question.id, optionIndex)}
                            disabled={showQuizResults || isReviewMode}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              ml: 1,
                              fontWeight: showQuizResults && isCorrect ? 'bold' : 'normal',
                              color: showQuizResults 
                                ? (currentBlock.block_type === 'test' && !testPassed)
                                  ? 'text.primary' // Don't highlight colors for failed tests
                                  : (isCorrect 
                                      ? 'success.main' 
                                      : isWrong 
                                        ? 'error.main' 
                                        : 'text.secondary')
                                : 'text.primary'
                            }}
                          >
                            {option}
                          </Typography>
                          {showQuizResults && testPassed && (
                            <>
                              {isCorrect && <CheckIcon color="success" sx={{ ml: 1 }} />}
                              {isWrong && <ClearIcon color="error" sx={{ ml: 1 }} />}
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Only show explanations if test is passed */}
                  {showQuizResults && testPassed && question.explanation && (
                    <Alert 
                      severity={testResults[question.id] ? 'success' : 'error'} 
                      sx={{ mt: 1 }}
                    >
                      <strong>Explanation:</strong> {question.explanation}
                    </Alert>
                  )}
                </Box>
              );
              })}

              {!showQuizResults && !isReviewMode ? (
                <Button
                  variant="contained"
                  onClick={handleQuizSubmit}
                  disabled={Object.keys(quizAnswers).length < testQuestions.length}
                >
                  Submit Final Assessment
                </Button>
              ) : showQuizResults ? (
                <Box>
                  {testPassed ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      🎉 Congratulations! You passed the final assessment with a score of {testScore}%.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Test score: {testScore}% (Passing score: {currentBlock.passing_score || 80}%)
                      {testScore > 0 && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          You can review the training content and retake the test.
                        </Typography>
                      )}
                    </Alert>
                  )}
                  
                  {testPassed ? (
                    <Button variant="contained" onClick={handleQuizContinue}>
                      Continue
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button variant="outlined" onClick={() => {
                        setShowQuizResults(false);
                        setQuizAnswers({});
                        setTestResults({});
                        setTestPassed(null);
                        setTestScore(0);
                      }}>
                        Retake Test
                      </Button>
                      <Button variant="contained" onClick={() => {
                        // Reset to first section with content blocks
                        let firstValidSectionIndex = 0;
                        while (firstValidSectionIndex < training.sections.length && 
                               (!training.sections[firstValidSectionIndex]?.content_blocks || 
                                training.sections[firstValidSectionIndex].content_blocks.length === 0)) {
                          firstValidSectionIndex++;
                        }
                        
                        if (firstValidSectionIndex < training.sections.length) {
                          setCurrentSectionIndex(firstValidSectionIndex);
                          setCurrentBlockIndex(0);
                          setShowQuizResults(false);
                          setQuizAnswers({});
                          setTestResults({});
                          setTestPassed(null);
                          setTestScore(0);
                        }
                      }}>
                        Review Training
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : null}
            </CardContent>
          </Card>
        );
      }

      default:
        return (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {currentBlock.title}
              </Typography>
              <Typography variant="body1">
                Content type not supported: {currentBlock.block_type}
              </Typography>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5">
              {training.title}
            </Typography>
            {isReviewMode && (
              <Chip 
                label="Review Mode" 
                color="info" 
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <LinearProgress 
          variant="determinate" 
          value={progressPercentage} 
          sx={{ mb: 1 }}
        />
        <Typography variant="body2" color="text.secondary">
          {isReviewMode 
            ? `Review Mode: All ${totalBlocks} blocks completed (100%)`
            : `Progress: ${completedBlocksCount} of ${totalBlocks} blocks completed (${Math.round(progressPercentage)}%)`
          }
        </Typography>
        {progressSaveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {progressSaveError}
          </Alert>
        )}
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, width: '100%' }}>
        <Grid container spacing={3} sx={{ width: '100%', maxWidth: '100%' }}>
          {/* Navigation Sidebar */}
          <Grid item xs={12} lg={3} md={4}>
            <Paper sx={{ p: 2, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom>
                Training Progress
              </Typography>
              
              {training.sections.map((section, sectionIndex) => {
                const sectionBlocks = section.content_blocks || [];
                const isActiveSection = sectionIndex === currentSectionIndex;
                const firstNavigableBlockIndex = getFirstNavigableBlockIndex(sectionIndex);
                const canNavigateSection = firstNavigableBlockIndex !== null;

                return (
                <Box key={section.id} sx={{ mb: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    component={canNavigateSection ? 'button' : 'p'}
                    onClick={
                      canNavigateSection
                        ? () => navigateToBlock(sectionIndex, firstNavigableBlockIndex!)
                        : undefined
                    }
                    sx={{ 
                      fontWeight: isActiveSection ? 'bold' : 'normal',
                      color: isActiveSection ? 'primary.main' : 'text.primary',
                      cursor: canNavigateSection ? 'pointer' : 'default',
                      textAlign: 'left',
                      width: '100%',
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      font: 'inherit',
                      display: 'block',
                      '&:hover': canNavigateSection
                        ? { textDecoration: 'underline' }
                        : undefined,
                    }}
                  >
                    Section {sectionIndex + 1}: {section.title}
                  </Typography>
                  
                  <Box sx={{ ml: 2 }}>
                    {sectionBlocks.map((block, blockIndex) => {
                      const isActiveBlock =
                        isActiveSection && blockIndex === currentBlockIndex;
                      const canNavigateBlock = canNavigateToBlock(sectionIndex, blockIndex);

                      return (
                      <Box
                        key={block.id}
                        component={canNavigateBlock ? 'button' : 'div'}
                        onClick={
                          canNavigateBlock
                            ? () => navigateToBlock(sectionIndex, blockIndex)
                            : undefined
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.5,
                          width: '100%',
                          cursor: canNavigateBlock ? 'pointer' : 'default',
                          color: isActiveBlock ? 'primary.main' : 'text.secondary',
                          fontWeight: isActiveBlock ? 'bold' : 'normal',
                          opacity: canNavigateBlock ? 1 : 0.55,
                          border: 'none',
                          background: 'none',
                          textAlign: 'left',
                          font: 'inherit',
                          borderRadius: 1,
                          '&:hover': canNavigateBlock
                            ? { backgroundColor: 'action.hover' }
                            : undefined,
                        }}
                      >
                        {getBlockIcon(block.block_type)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {block.title}
                        </Typography>
                        {completedBlocks.has(block.id) && (
                          <CheckIcon color="success" sx={{ ml: 'auto', fontSize: 'small' }} />
                        )}
                      </Box>
                    );
                    })}
                  </Box>
                </Box>
              );
              })}
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} lg={9} md={8}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  {currentSection?.title} - {currentBlock?.title}
                </Typography>
                <Chip 
                  label={currentBlock?.block_type} 
                  size="small" 
                  icon={getBlockIcon(currentBlock?.block_type || 'text')}
                />
              </Box>

              {renderContentBlock()}

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<PrevIcon />}
                  onClick={handlePrevious}
                  disabled={currentSectionIndex === 0 && currentBlockIndex === 0}
                >
                  Previous
                </Button>

                {(currentBlock?.block_type !== 'quiz' && currentBlock?.block_type !== 'test') && (
                  <Button
                    variant="contained"
                    endIcon={<NextIcon />}
                    onClick={handleBlockComplete}
                    disabled={
                      (currentBlock?.block_type === 'link' && 
                      currentBlock?.content?.requires_certification && 
                       !linkCertifications[currentBlock.id]) ||
                      (currentBlock?.block_type === 'video' &&
                       !videoCompletions[String(currentBlock.id)]?.watched &&
                       (videoProgress.duration === 0 || // Video not loaded/started yet
                        (videoProgress.duration - videoProgress.currentTime) > 30)) // Or more than 30 seconds remaining (video time, not real time)
                    }
                  >
                    {currentSectionIndex === training.sections.length - 1 && 
                     currentBlockIndex === (currentSection?.content_blocks?.length || 0) - 1 
                      ? 'Complete Training' 
                      : 'Next'}
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TrainingPlayer;